import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import {
  useSettingsBookingPages,
  useAddBookingPage,
  useUpdateBookingPage,
  useDeleteBookingPage,
  useBookingPageLogs,
} from '@/v2/hooks/settings/useSettingsBookingPages'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import FieldLabel from '@/v2/components/common/FieldLabel'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import {
  SettingsPage,
  SettingsEmptyState,
  PrimaryButton,
  SecondaryButton,
  HeaderActions,
  CardGrid,
  Card,
  CardTitle,
  CardDescription,
  CardActions,
  ActionButton,
} from '@/v2/components/settings/shared'

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;

  ${props =>
    props.active
      ? `
    color: #059669;
  `
      : `
    color: #dc2626;
  `}
`

const StatusIcon = styled.div`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;

  ${props =>
    props.active
      ? `
    background-color: #059669;
  `
      : `
    background-color: #dc2626;
  `}
`

const InputGroup = styled.div`
  display: flex;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  overflow: hidden;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }
`

const InputPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #f9fafb;
  color: #6b7280;
  font-size: 0.875rem;
  border-right: 1px solid #d1d5db;
`

const InputField = styled.input`
  flex: 1;
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
  }
`

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-left: none;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`

const LogsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }

  th {
    font-weight: 600;
    color: #374151;
    background-color: #f9fafb;
  }

  td {
    color: #4b5563;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const LogsFilters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`

const INITIAL_FORM = {
  title: '',
  description: '',
  slug: '',
  active: true,
  bookingWindow: 30,
  slotThrottlingEnabled: false,
  slotThrottlingSmartVariance: false,
  maxVisibleSlotsPerDay: '',
  slotThrottlingShowAllAfterEnabled: false,
  slotThrottlingShowAllAfterHour: '',
  slotThrottlingSkipVirtual: false,
}

const BookingPages = () => {
  const { bookingPages: rawPages, isLoading } = useSettingsBookingPages()
  const { addBookingPage, isPending: isAdding } = useAddBookingPage()
  const { updateBookingPage, isPending: isUpdating } = useUpdateBookingPage()
  const { deleteBookingPage } = useDeleteBookingPage()
  const { onOpenSnackbar } = useSnackbarContext()

  const [showLogsModal, setShowLogsModal] = useState(false)
  const [logsFilters, setLogsFilters] = useState({ date: '', email: '' })

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForCreate,
    openForEdit,
    close,
    updateField,
  } = useFormModal(INITIAL_FORM)

  const emptyFilters = useMemo(() => ({}), [])
  const { logs, isLoading: isLoadingLogs } = useBookingPageLogs(
    showLogsModal ? logsFilters : emptyFilters
  )

  const isSaving = isAdding || isUpdating

  const bookingPages = useMemo(
    () =>
      rawPages.map(page => ({
        id: page.id,
        title: page.title || '',
        description: page.description || '',
        slug: page.slug || '',
        active: page.active !== undefined ? page.active : true,
        bookingWindow: page.bookingWindow || 30,
        slotThrottlingEnabled: page.slotThrottlingEnabled || false,
        slotThrottlingSmartVariance: page.slotThrottlingSmartVariance || false,
        maxVisibleSlotsPerDay: page.maxVisibleSlotsPerDay,
        slotThrottlingShowAllAfterEnabled:
          page.slotThrottlingShowAllAfterEnabled || false,
        slotThrottlingShowAllAfterHour: page.slotThrottlingShowAllAfterHour,
        slotThrottlingSkipVirtual: page.slotThrottlingSkipVirtual || false,
      })),
    [rawPages]
  )

  const bookingWindowOptions = [
    { value: '7', label: '7 days from now' },
    { value: '15', label: '15 days from now' },
    { value: '30', label: '30 days from now' },
    { value: '45', label: '45 days from now' },
    { value: '60', label: '60 days from now' },
    { value: '90', label: '90 days from now' },
    { value: '120', label: '120 days from now' },
  ]

  const handleEdit = page => {
    openForEdit(page, p => ({
      title: p.title,
      description: p.description,
      slug: p.slug,
      active: p.active,
      bookingWindow: p.bookingWindow,
      slotThrottlingEnabled: p.slotThrottlingEnabled,
      slotThrottlingSmartVariance: p.slotThrottlingSmartVariance,
      maxVisibleSlotsPerDay: p.maxVisibleSlotsPerDay ?? '',
      slotThrottlingShowAllAfterEnabled: p.slotThrottlingShowAllAfterEnabled,
      slotThrottlingShowAllAfterHour: p.slotThrottlingShowAllAfterHour ?? '',
      slotThrottlingSkipVirtual: p.slotThrottlingSkipVirtual,
    }))
  }

  const handleDelete = async pageId => {
    if (confirm('Are you sure you want to delete this booking page?')) {
      try {
        await deleteBookingPage(pageId)
        onOpenSnackbar('Booking page deleted successfully', 'success')
      } catch (error) {
        onOpenSnackbar('Failed to delete booking page', 'error')
      }
    }
  }

  const handleSubmit = async () => {
    const pageData = {
      title: formData.title,
      description: formData.description,
      slug: formData.slug,
      active: formData.active,
      booking_window: parseInt(formData.bookingWindow),
      slot_throttling_enabled: formData.slotThrottlingEnabled,
      slot_throttling_smart_variance: formData.slotThrottlingSmartVariance,
      max_visible_slots_per_day: formData.maxVisibleSlotsPerDay
        ? parseInt(formData.maxVisibleSlotsPerDay)
        : null,
      slot_throttling_show_all_after_enabled:
        formData.slotThrottlingShowAllAfterEnabled,
      slot_throttling_show_all_after_hour: formData.slotThrottlingShowAllAfterHour
        ? parseInt(formData.slotThrottlingShowAllAfterHour)
        : null,
      slot_throttling_skip_virtual: formData.slotThrottlingSkipVirtual,
    }

    try {
      if (isEditing) {
        await updateBookingPage({ id: editingItem.id, ...pageData })
        onOpenSnackbar('Booking page updated successfully', 'success')
      } else {
        await addBookingPage(pageData)
        onOpenSnackbar('Booking page created successfully', 'success')
      }
      close()
    } catch (error) {
      onOpenSnackbar('Failed to save booking page', 'error')
    }
  }

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    onOpenSnackbar('Copied to clipboard!', 'success')
  }

  const getBookingPageUrl = slug => {
    const { hostname } = window.location
    const baseUrl =
      hostname === 'localhost' || hostname.startsWith('127')
        ? 'https://demo.clearworks.com'
        : `${window.location.protocol}//${hostname}`
    return `${baseUrl}/booking/${slug}`
  }

  const formatDateTime = dateString => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  return (
    <SettingsPage
      title="Booking pages"
      subtitle="Create and manage patient booking portals"
      loading={isLoading}
      actions={
        <HeaderActions>
          <SecondaryButton onClick={() => setShowLogsModal(true)}>
            View Logs
          </SecondaryButton>
          <PrimaryButton onClick={openForCreate}>
            + New Booking Page
          </PrimaryButton>
        </HeaderActions>
      }
    >
      {bookingPages.length === 0 ? (
        <SettingsEmptyState
          icon={
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
            </svg>
          }
          heading="You haven't added any booking pages yet"
          description={
            <>
              Click on <strong>New booking page</strong> to add the first one
            </>
          }
          actionLabel="New booking page"
          onAction={openForCreate}
        />
      ) : (
        <CardGrid>
          {bookingPages.map(page => (
            <Card key={page.id} onClick={() => handleEdit(page)}>
              <CardTitle title={page.title}>{page.title}</CardTitle>
              <CardDescription>
                {page.description.length > 100
                  ? `${page.description.substring(0, 100)}...`
                  : page.description}
              </CardDescription>
              <StatusIndicator active={page.active}>
                <StatusIcon active={page.active} />
                {page.active ? 'Active' : 'Inactive'}
              </StatusIndicator>
              <CardActions
                onClick={e => e.stopPropagation()}
                style={{ marginTop: '1rem' }}
              >
                <ActionButton onClick={() => handleEdit(page)}>
                  Edit
                </ActionButton>
                <ActionButton
                  $variant="danger"
                  onClick={() => handleDelete(page.id)}
                >
                  Delete
                </ActionButton>
              </CardActions>
            </Card>
          ))}
        </CardGrid>
      )}

      <Modal
        isOpen={showModal}
        title={isEditing ? 'Edit Booking Page' : 'New Booking Page'}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel={isEditing ? 'Save Changes' : 'Create Booking Page'}
        cancelLabel="Cancel"
        submitDisabled={!formData.title || isSaving}
        loading={false}
        maxWidth="sm"
        fullWidth
        showExtraButton={isEditing}
        extraButtonLabel="Delete Booking Page"
        extraButtonColor="error"
        onExtraButtonClick={() => {
          handleDelete(editingItem.id)
          close()
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <CheckboxField
            label="Active"
            checked={formData.active}
            onChange={e => updateField('active', e.target.checked)}
          />

          <TextField
            fullWidth
            label="Booking page title"
            placeholder="Clinic's booking page"
            value={formData.title}
            onChange={val => updateField('title', val)}
          />

          <TextField
            fullWidth
            label="Booking page description"
            placeholder="Clinic's appointment description"
            value={formData.description}
            onChange={val => updateField('description', val)}
          />

          <Box>
            <FieldLabel>Public link</FieldLabel>
            <Box sx={{ mt: '8px' }}>
              {!isEditing ? (
                <>
                  <InputGroup>
                    <InputPrefix>{getBookingPageUrl('')}</InputPrefix>
                    <InputField
                      type="text"
                      value={formData.slug}
                      onChange={e => updateField('slug', e.target.value)}
                      placeholder="first-consult"
                      required
                    />
                  </InputGroup>
                  <p
                    style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                    }}
                  >
                    This is where your patients go to schedule an appointment.
                    It is not possible to edit this field.
                  </p>
                </>
              ) : (
                <>
                  <InputGroup>
                    <InputField
                      type="text"
                      value={getBookingPageUrl(editingItem.slug)}
                      disabled
                      style={{ backgroundColor: '#f9fafb', color: '#6b7280' }}
                    />
                    <CopyButton
                      type="button"
                      onClick={() =>
                        copyToClipboard(getBookingPageUrl(editingItem.slug))
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                      </svg>
                      Copy
                    </CopyButton>
                  </InputGroup>
                  <p
                    style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                    }}
                  >
                    This is where your patients go to schedule an appointment.
                  </p>
                </>
              )}
            </Box>
          </Box>

          <Box>
            <SelectField
              fullWidth
              label="Booking window"
              options={bookingWindowOptions}
              value={String(formData.bookingWindow)}
              onChange={val => updateField('bookingWindow', parseInt(val))}
            />
            <p
              style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginTop: '0.25rem',
              }}
            >
              Patients can't schedule beyond Booking availability
            </p>
          </Box>

          <Box>
            <CheckboxField
              label="Enable slot throttling"
              checked={formData.slotThrottlingEnabled}
              onChange={e =>
                updateField('slotThrottlingEnabled', e.target.checked)
              }
            />
            <p
              style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginTop: '0.25rem',
              }}
            >
              Limit the number of visible appointment slots shown to patients
              per day
            </p>
          </Box>

          {formData.slotThrottlingEnabled && (
            <>
              <TextField
                fullWidth
                label="Max visible slots per day"
                type="number"
                inputProps={{ min: 2 }}
                placeholder="e.g. 10"
                value={formData.maxVisibleSlotsPerDay}
                onChange={val => updateField('maxVisibleSlotsPerDay', val)}
              />
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '-0.5rem',
                }}
              >
                Minimum of 2 slots. Leave empty for unlimited.
              </p>

              <CheckboxField
                label="Enable smart variance"
                checked={formData.slotThrottlingSmartVariance}
                onChange={e =>
                  updateField('slotThrottlingSmartVariance', e.target.checked)
                }
              />
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '-0.5rem',
                }}
              >
                Adjust visible slots per day based on historical occupancy
                patterns. Busier days show fewer slots, quieter days show more.
              </p>

              <CheckboxField
                label="Show all slots after a specific hour"
                checked={formData.slotThrottlingShowAllAfterEnabled}
                onChange={e =>
                  updateField(
                    'slotThrottlingShowAllAfterEnabled',
                    e.target.checked
                  )
                }
              />
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '-0.5rem',
                }}
              >
                Disable throttling and show all remaining slots after a certain
                hour of the day.
              </p>

              {formData.slotThrottlingShowAllAfterEnabled && (
                <>
                  <SelectField
                    fullWidth
                    noMargin
                    label="Show all slots after"
                    placeholder="Choose an hour"
                    options={Array.from({ length: 24 }, (_, i) => {
                      const period = i < 12 ? 'AM' : 'PM'
                      const hour = i === 0 ? 12 : i > 12 ? i - 12 : i
                      return { value: String(i), label: `${hour}:00 ${period}` }
                    })}
                    value={String(formData.slotThrottlingShowAllAfterHour)}
                    onChange={val =>
                      updateField(
                        'slotThrottlingShowAllAfterHour',
                        parseInt(val)
                      )
                    }
                  />
                  <p
                    style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginTop: '-0.5rem',
                    }}
                  >
                    All slots after the selected hour will be shown without
                    throttling.
                  </p>
                </>
              )}

              <CheckboxField
                label="Skip throttling for virtual appointments"
                checked={formData.slotThrottlingSkipVirtual}
                onChange={e =>
                  updateField('slotThrottlingSkipVirtual', e.target.checked)
                }
              />
              <p
                style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  marginTop: '-0.5rem',
                }}
              >
                Virtual (remote) appointments will show all available slots
                without throttling.
              </p>
            </>
          )}
        </Box>
      </Modal>

      <Modal
        isOpen={showLogsModal}
        title="Booking Page Logs"
        onClose={() => setShowLogsModal(false)}
        maxWidth="md"
        fullWidth
        showConfirm={false}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <LogsFilters>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Date"
                placeholder="YYYY-MM-DD"
                value={logsFilters.date}
                onChange={val =>
                  setLogsFilters(prev => ({ ...prev, date: val }))
                }
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Email"
                placeholder="patient@example.com"
                value={logsFilters.email}
                onChange={val =>
                  setLogsFilters(prev => ({ ...prev, email: val }))
                }
              />
            </Box>
          </LogsFilters>

          {isLoadingLogs ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}
            >
              Loading logs...
            </div>
          ) : logs.length === 0 ? (
            <div
              style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}
            >
              No logs found
            </div>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <LogsTable>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Created At</th>
                    <th>Booking Page</th>
                    <th>Patient</th>
                    <th>Appointment</th>
                    <th>Phone Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td>{formatDateTime(log.createdAt)}</td>
                      <td>{log.bookingPage ? log.bookingPage.title : '-'}</td>
                      <td>
                        {log.patient
                          ? `${log.patient.fullName} (${log.patient.email})`
                          : '-'}
                      </td>
                      <td>
                        {log.appointment
                          ? formatDateTime(log.appointment.startsAt)
                          : '-'}
                      </td>
                      <td>{formatDateTime(log.phoneVerifiedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </LogsTable>
            </Box>
          )}
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default BookingPages
